import React from 'react';
import PropTypes from 'prop-types';
import { Spacing, Text } from '@reservamos/elements';
import LocalizedCurrency from 'components/LocalizedCurrency';

/**
 * LoyaltyPricingRow component.
 * Render a pricing row for loyalty points with description and formatted currency or points display.
 * @param {object} props - description, currency, bold, canApplyPoints, doterPointsLabel, highlightColor, size, mobileSize.
 * @param {string} props.description - The description text to display.
 * @param {string|number} props.currency - The currency value or points amount to display.
 * @param {boolean} props.bold - Whether to display text in bold weight.
 * @param {boolean} props.canApplyPoints - Whether points can be applied (shows currency format).
 * @param {string} props.doterPointsLabel - The label text for doter points.
 * @param {boolean} props.highlightColor - Whether to use highlight color styling.
 * @param {string} props.size - The font size.
 * @param {string} props.mobileSize - The mobile font size.
 * @returns {JSX.Element} The rendered loyalty pricing row component.
 */
const LoyaltyPricingRow = ({
  description,
  currency,
  bold,
  canApplyPoints,
  doterPointsLabel,
  highlightColor,
  size,
  mobileSize,
}) => {
  const textColor = highlightColor ? 'loyaltyLight' : 'grayStrong';
  return (
    <Spacing justifyContent="space-between" alignItems="center" fullWidth>
      <Text weight={bold ? 'bold' : 'regular'} size={size} mobileSize={mobileSize}>
        {description}
      </Text>

      {canApplyPoints ? (
        <LocalizedCurrency
          price={currency}
          currencyStyle="currency"
          size={size}
          mobileSize={mobileSize}
          weight={bold ? 'bold' : 'regular'}
        />
      ) : (
        <Text
          weight={bold || highlightColor ? 'bold' : 'regular'}
          size={size}
          mobileSize={mobileSize}
          color={textColor}
        >
          {currency} {doterPointsLabel}
        </Text>
      )}
    </Spacing>
  );
};

LoyaltyPricingRow.propTypes = {
  description: PropTypes.string.isRequired,
  currency: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  bold: PropTypes.bool,
  canApplyPoints: PropTypes.bool,
  highlightColor: PropTypes.bool,
  doterPointsLabel: PropTypes.string,
  size: PropTypes.string,
  mobileSize: PropTypes.string,
};

LoyaltyPricingRow.defaultProps = {
  bold: false,
  canApplyPoints: false,
  doterPointsLabel: '',
  highlightColor: '',
  size: 'M',
  mobileSize: 'S',
};

export default LoyaltyPricingRow;
